import { Order as OrderType } from "../utils/types";

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: OrderType;
}

const OrderDetailsModal = ({ isOpen, onClose, order }: OrderDetailsModalProps) => {
  if (!isOpen) return null;

  // Format date
  const formatFullDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Format time
  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Get status display text
  const getStatusDisplay = () => {
    return order.orderStatus.charAt(0).toUpperCase() + order.orderStatus.slice(1).replace(/_/g, ' ');
  };

  // Open Google Maps with the delivery address
  const openGoogleMaps = () => {
    const address = `${order.deliveryAddress.addressLine1}, ${order.deliveryAddress.addressLine2 || ''}, ${order.deliveryAddress.city}, ${order.deliveryAddress.pincode}`;
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    window.open(googleMapsUrl, '_blank');
  };

  // Calculate subtotal (total - tax - delivery fee for now, you can adjust based on your data)
  const subtotal = order.totalAmount - 7 - 60; // Temporary calculation
  const tax = 7; // You might want to get this from order data
  const deliveryFee = 60; // You might want to get this from order data

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-0 md:p-4">
      <div className="bg-white dark:bg-gray-800 w-full h-full md:h-auto md:max-h-[90vh] md:max-w-2xl md:rounded-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 z-10 px-4 py-4 border-b dark:border-gray-700 flex items-center justify-between">
          <button onClick={onClose} className="p-1 dark:text-gray-200">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <h2 className="text-lg font-medium font-Urbanist dark:text-gray-200">Order #{order._id.slice(-6).toUpperCase()}</h2>
          <button onClick={onClose} className="p-1 dark:text-gray-200">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/>
            </svg>
          </button>
        </div>

        {/* Map Section */}
        <div className="relative">
          <div className="w-full h-64 bg-gradient-to-br from-teal-900 via-teal-800 to-teal-700 relative overflow-hidden">
            {/* Simplified map visualization */}
            <div className="absolute inset-0 opacity-30">
              <svg className="w-full h-full" viewBox="0 0 400 300">
                {/* Map outline paths - simplified india shape */}
                <path d="M100,50 L150,40 L200,60 L250,50 L300,70 L320,120 L310,180 L280,220 L240,240 L200,250 L160,240 L120,220 L90,180 L80,120 Z" 
                  fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
                <path d="M120,80 L140,75 L160,85 L180,80 L200,90 L210,110 L205,140 L190,160 L170,170 L150,165 L130,155 L115,130 Z" 
                  fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5"/>
              </svg>
            </div>
            
            {/* Location marker */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
              </div>
            </div>

            {/* Google Maps Logo */}
            <div className="absolute bottom-4 left-4 text-white text-xs font-bold bg-black bg-opacity-30 px-2 py-1 rounded">
              Google
            </div>
          </div>

          {/* Address card overlay */}
          <div className="absolute bottom-0 left-0 right-0 mx-4 mb-4 bg-white dark:bg-gray-700 rounded-xl shadow-lg p-4 flex items-center justify-between">
            <div className="flex items-start gap-3 flex-1">
              <div className="mt-1">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="#FF8A00">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-200 font-Urbanist line-clamp-2">
                  {order.deliveryAddress.addressLine1}
                  {order.deliveryAddress.addressLine2 && `, ${order.deliveryAddress.addressLine2}`}, {order.deliveryAddress.city}
                </p>
              </div>
            </div>
            <button 
              onClick={openGoogleMaps}
              className="ml-2 bg-[#FF8A00] text-white px-4 py-2 rounded-full text-sm font-medium font-Urbanist whitespace-nowrap flex items-center gap-1"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white" strokeWidth="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/>
              </svg>
              Open
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Customer Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-base font-medium font-Urbanist dark:text-gray-200 mb-4">Customer</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6A6A6A" strokeWidth="2" className="dark:invert">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"/>
                </svg>
                <span className="text-sm text-gray-500 dark:text-gray-400 font-Urbanist">Name</span>
                <span className="text-sm font-medium ml-auto font-Urbanist dark:text-gray-200">{order.deliveryAddress.fullName}</span>
              </div>
              <div className="flex items-center gap-3">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6A6A6A" strokeWidth="2" className="dark:invert">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                <span className="text-sm text-gray-500 dark:text-gray-400 font-Urbanist">Phone</span>
                <span className="text-sm font-medium ml-auto font-Urbanist dark:text-gray-200">{order.deliveryAddress.phone}</span>
              </div>
              <div className="flex items-start gap-3">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6A6A6A" strokeWidth="2" className="mt-0.5 dark:invert">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <span className="text-sm text-gray-500 dark:text-gray-400 font-Urbanist">Address</span>
                <span className="text-sm font-medium ml-auto text-right font-Urbanist dark:text-gray-200 max-w-[180px]">
                  {order.deliveryAddress.addressLine1}, {order.deliveryAddress.city}
                </span>
              </div>
            </div>
          </div>

          {/* Order Info Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-base font-medium font-Urbanist dark:text-gray-200 mb-4">Order Info</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6A6A6A" strokeWidth="2" className="dark:invert">
                  <path d="M3 3h18v18H3zM8 3v18M16 3v18M3 8h18M3 16h18"/>
                </svg>
                <span className="text-sm text-gray-500 dark:text-gray-400 font-Urbanist">Order ID</span>
                <span className="text-sm font-medium ml-auto font-Urbanist dark:text-gray-200">{order._id.slice(-10).toUpperCase()}</span>
              </div>
              <div className="flex items-center gap-3">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6A6A6A" strokeWidth="2" className="dark:invert">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
                <span className="text-sm text-gray-500 dark:text-gray-400 font-Urbanist">Status</span>
                <span className="text-sm font-medium ml-auto px-3 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-200 rounded-full font-Urbanist">
                  ● {getStatusDisplay()}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6A6A6A" strokeWidth="2" className="dark:invert">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                <span className="text-sm text-gray-500 dark:text-gray-400 font-Urbanist">Placed At</span>
                <span className="text-sm font-medium ml-auto font-Urbanist dark:text-gray-200">
                  {formatFullDate(order.createdAt)}, {formatTime(order.createdAt)}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6A6A6A" strokeWidth="2" className="dark:invert">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
                <span className="text-sm text-gray-500 dark:text-gray-400 font-Urbanist">Payment</span>
                <span className="text-sm font-medium ml-auto font-Urbanist dark:text-gray-200">
                  {order.paymentMethod} — pending
                </span>
              </div>
            </div>
          </div>

          {/* Items Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-base font-medium font-Urbanist dark:text-gray-200 mb-4">Items ({order.items.length})</h3>
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[#FF8A00] font-medium font-Urbanist">× {item.quantity}</span>
                    <span className="text-sm font-medium font-Urbanist dark:text-gray-200">{item.dishName}</span>
                  </div>
                  <span className="text-sm font-medium font-Urbanist dark:text-gray-200">₹{item.price * item.quantity}.00</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bill Summary Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h3 className="text-base font-medium font-Urbanist dark:text-gray-200 mb-4">Bill Summary</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400 font-Urbanist">Subtotal</span>
                <span className="text-sm font-medium font-Urbanist dark:text-gray-200">₹{subtotal}.00</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400 font-Urbanist">Tax</span>
                <span className="text-sm font-medium font-Urbanist dark:text-gray-200">₹{tax}.00</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400 font-Urbanist">Delivery Fee</span>
                <span className="text-sm font-medium font-Urbanist dark:text-gray-200">₹{deliveryFee}.00</span>
              </div>
              <div className="border-t dark:border-gray-700 pt-3 flex items-center justify-between">
                <span className="text-lg font-semibold font-Urbanist dark:text-gray-200">Total</span>
                <span className="text-lg font-bold text-[#FF8A00] font-Urbanist">₹{order.totalAmount}.00</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsModal;
